import { useRef, useEffect, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ENGINE_SMOKE_CONSTANTS } from '../constants';

const vertexShader = `
attribute float size;
attribute float opacity;
varying float vOpacity;

void main() {
    vOpacity = opacity;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
}
`;

const fragmentShader = `
varying float vOpacity;

void main() {
    float r = length(gl_PointCoord - vec2(0.5));
    if (r > 0.5) discard;
    
    float alpha = smoothstep(0.5, 0.0, r);
    gl_FragColor = vec4(vec3(${ENGINE_SMOKE_CONSTANTS.VISUAL.COLOR.join(',')}), alpha * vOpacity);
}
`;

export default function ExhaustSmoke({ isEngineOn, groupRef, soundGraphics }) {
    const meshRef = useRef();
    const engineStartTime = useRef(0);
    const [shouldEmitSmoke, setShouldEmitSmoke] = useState(false);
    const exhaustRef = useRef();
    const originalRotation = useRef(null);
    const originalPosition = useRef(null);

    // Handle engine state changes
    useEffect(() => {
        if (isEngineOn) {
            engineStartTime.current = Date.now();
            setShouldEmitSmoke(false);
        } else {
            setShouldEmitSmoke(false);
        }
    }, [isEngineOn]);

    // Update group position and rotation from constants
    useEffect(() => {
        if (groupRef.current) {
            groupRef.current.position.set(...ENGINE_SMOKE_CONSTANTS.INITIAL_POSITION);
            groupRef.current.rotation.set(...ENGINE_SMOKE_CONSTANTS.INITIAL_ROTATION);
        }
    }, [groupRef]);
    
    // Create particles
    const positions = useMemo(() => new Float32Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT * 3), []);
    const sizes = useMemo(() => new Float32Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT), []);
    const opacities = useMemo(() => new Float32Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT), []);
    
    // Particle system parameters
    const params = useMemo(() => ({
        velocities: Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT).fill().map(() => ({
            x: (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.VELOCITY.HORIZONTAL_SPREAD,
            y: Math.random() * (ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MAX - ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MIN) + ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MIN,
            z: (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.VELOCITY.DEPTH_SPREAD
        })),
        lifetimes: Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT).fill().map(() => Math.random() * ENGINE_SMOKE_CONSTANTS.MAX_LIFETIME),
        emitDelays: Array(ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT).fill().map(() => Math.random() * ENGINE_SMOKE_CONSTANTS.EMIT_DELAY_RANGE)
    }), []);

    useFrame((state, delta) => {
        if (!meshRef.current) return;

        // Check if we should start emitting smoke
        if (isEngineOn && !shouldEmitSmoke) {
            const timeSinceStart = (Date.now() - engineStartTime.current) / 1000;
            if (timeSinceStart >= ENGINE_SMOKE_CONSTANTS.INITIAL_ENGINE_DELAY) {
                setShouldEmitSmoke(true);
            }
        }

        const positions = meshRef.current.geometry.attributes.position.array;
        const sizes = meshRef.current.geometry.attributes.size.array;
        const opacities = meshRef.current.geometry.attributes.opacity.array;

        for (let i = 0; i < ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT; i++) {
            const i3 = i * 3;
            
            // Only update particles if engine is on and after initial delay
            if (shouldEmitSmoke) {
                params.lifetimes[i] += delta;
            }

            if (params.lifetimes[i] > ENGINE_SMOKE_CONSTANTS.MAX_LIFETIME) {
                // Reset particle
                positions[i3] = 0;
                positions[i3 + 1] = 0;
                positions[i3 + 2] = 0;
                sizes[i] = 0;
                opacities[i] = 0;
                params.lifetimes[i] = 0;

                // Randomize velocity slightly on reset
                params.velocities[i] = {
                    x: (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.VELOCITY.HORIZONTAL_SPREAD,
                    y: Math.random() * (ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MAX - ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MIN) + ENGINE_SMOKE_CONSTANTS.VELOCITY.UPWARD_MIN,
                    z: (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.VELOCITY.DEPTH_SPREAD
                };
            } else if (shouldEmitSmoke && params.lifetimes[i] > params.emitDelays[i]) {
                // Update particle only if we should emit smoke
                positions[i3] += params.velocities[i].x;
                positions[i3 + 1] += params.velocities[i].y;
                positions[i3 + 2] += params.velocities[i].z;

                // Add subtle turbulence
                params.velocities[i].x += (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.TURBULENCE;
                params.velocities[i].z += (Math.random() - 0.5) * ENGINE_SMOKE_CONSTANTS.TURBULENCE;

                const lifeProgress = (params.lifetimes[i] - params.emitDelays[i]) / ENGINE_SMOKE_CONSTANTS.MAX_LIFETIME;
                opacities[i] = Math.sin(lifeProgress * Math.PI) * ENGINE_SMOKE_CONSTANTS.VISUAL.MAX_OPACITY;
                sizes[i] = Math.min(ENGINE_SMOKE_CONSTANTS.VISUAL.MAX_SIZE, lifeProgress * ENGINE_SMOKE_CONSTANTS.VISUAL.SIZE_MULTIPLIER);
            }
        }

        meshRef.current.geometry.attributes.position.needsUpdate = true;
        meshRef.current.geometry.attributes.size.needsUpdate = true;
        meshRef.current.geometry.attributes.opacity.needsUpdate = true;
    });

    useEffect(() => {
        if (exhaustRef.current) {
            originalRotation.current = exhaustRef.current.rotation.clone();
        }
    }, []);

    useFrame(() => {
        if (!groupRef.current || !isEngineOn || !soundGraphics) return;

        // Get sound data
        const soundData = soundGraphics.getAllFrequencyData();
        
        // Use bass frequencies (0-200Hz) and volume for shaking
        const bassIntensity = soundData.bass.average / 255; // 0 to 1
        const volumeIntensity = soundData.volume / 128; // 0 to 1
        
        // Calculate shake intensity based on both bass and volume
        const shakeIntensity = (bassIntensity * 0.8 + volumeIntensity * 0.2) * 0.05;
        
        // Apply directional shaking based on sound
        if (originalPosition.current) {
            // Horizontal shake based on bass
            groupRef.current.position.x = originalPosition.current.x + 
                Math.sin(Date.now() * 0.05) * shakeIntensity * 2;
            
            // Vertical shake based on volume
            groupRef.current.position.y = originalPosition.current.y + 
                Math.cos(Date.now() * 0.03) * volumeIntensity * 0.03;
            
            // Forward/back shake based on bass
            groupRef.current.position.z = originalPosition.current.z + 
                Math.sin(Date.now() * 0.04) * bassIntensity * 0.04;

            // Add some rotation shake for more effect
            groupRef.current.rotation.z = Math.sin(Date.now() * 0.03) * shakeIntensity * 0.5;
        }

        // Also affect the particle emission
        if (meshRef.current && shouldEmitSmoke) {
            // Modify particle velocities based on sound
            const positions = meshRef.current.geometry.attributes.position.array;
            for (let i = 0; i < ENGINE_SMOKE_CONSTANTS.PARTICLE_COUNT; i++) {
                const i3 = i * 3;
                // Add extra horizontal spread based on bass
                positions[i3] += (Math.random() - 0.5) * bassIntensity * 0.1;
                // Add extra upward velocity based on volume
                positions[i3 + 1] += volumeIntensity * 0.05;
            }
            meshRef.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    useEffect(() => {
        if (groupRef.current) {
            originalPosition.current = groupRef.current.position.clone();
        }
    }, [groupRef]);

    const geometry = useMemo(() => {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        geometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));
        return geometry;
    }, [positions, sizes, opacities]);

    return (
        <group ref={groupRef}>
            <points ref={meshRef}>
                <primitive object={geometry} attach="geometry" />
                <shaderMaterial
                    attach="material"
                    vertexShader={vertexShader}
                    fragmentShader={fragmentShader}
                    transparent
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </points>
        </group>
    );
} 